import {
  buildLinkQuestionMappings,
  type LinkQuestionMapping,
  type QuestionField,
  type QuestionMapping,
  type ReviewItem
} from './shareItemMapping';

interface ShareItemService {
  updateItem: (itemId: number, payload: { questionsSchema: { fields: QuestionField[] } }) => Promise<unknown>;
  linkItems: (
    sourceItemId: number,
    targetItemId: number,
    mappings: LinkQuestionMapping[],
    options: {
      includeRoom: boolean;
      includePhotos: boolean;
      includeFloor: boolean;
    }
  ) => Promise<unknown>;
  createItem: (payload: {
    name: string;
    description?: string;
    categoryId: number;
    questionsSchema: { fields: QuestionField[] };
    displayOrder: number;
    isRequired: boolean;
    defaultPresent: boolean;
  }) => Promise<ReviewItem>;
}

export const createShareLink = async ({
  service,
  sourceItem,
  targetItem,
  sourceFields,
  questionMappings,
  questionsToCopy,
  includeRoom,
  includePhotos,
  includeFloor
}: {
  service: ShareItemService;
  sourceItem: ReviewItem;
  targetItem: ReviewItem;
  sourceFields: QuestionField[];
  questionMappings: QuestionMapping[];
  questionsToCopy: Set<string>;
  includeRoom: boolean;
  includePhotos: boolean;
  includeFloor: boolean;
}): Promise<{ mappings: LinkQuestionMapping[] }> => {
  if (questionsToCopy.size > 0) {
    const copiedQuestions = sourceFields.filter((field) => questionsToCopy.has(field.id));

    await service.updateItem(targetItem.id, {
      questionsSchema: {
        fields: [
          ...(targetItem.questionsSchema?.fields || []),
          ...copiedQuestions
        ]
      }
    });
  }

  const mappings = buildLinkQuestionMappings(questionMappings, questionsToCopy, sourceItem.name);

  await service.linkItems(sourceItem.id, targetItem.id, mappings, {
    includeRoom,
    includePhotos,
    includeFloor
  });

  return { mappings };
};

export const createMetadataOnlyShareLink = async ({
  service,
  sourceItem,
  targetItem
}: {
  service: Pick<ShareItemService, 'linkItems'>;
  sourceItem: ReviewItem;
  targetItem: ReviewItem;
}): Promise<void> => {
  await service.linkItems(sourceItem.id, targetItem.id, [], {
    includeRoom: true,
    includePhotos: true,
    includeFloor: true
  });
};

export const createShareTargetItem = ({
  service,
  categoryId,
  name,
  description
}: {
  service: Pick<ShareItemService, 'createItem'>;
  categoryId: number;
  name: string;
  description: string;
}): Promise<ReviewItem> => {
  return service.createItem({
    name: name.trim(),
    description: description.trim() || undefined,
    categoryId,
    questionsSchema: { fields: [] },
    displayOrder: 999,
    isRequired: false,
    defaultPresent: true
  });
};

interface ShareActionErrorLike {
  message?: string;
  response?: {
    data?: {
      message?: string;
    };
  };
}

export const getShareActionErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error !== 'object' || error === null) {
    return fallback;
  }

  const shareError = error as ShareActionErrorLike;
  return shareError.response?.data?.message || shareError.message || fallback;
};
